import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { Button } from "../../../components/core/basic/Button";
import { Table } from "../../../components/table/Table";
import { RT8Pagination } from "../../../components/table/Pagination";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { MdRotateLeft } from "react-icons/md";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import { selectionStateToSelectedIds, resetTable } from "../../../components/table/utils";
import { Switch } from "../../../components/fields/base/Switch";
import { useSimpleCan } from "../../../config/casl/casl";
import { useAppSelector } from "../../../redux/hooks";
import { ProgramContextProvider } from "../../../components/custom/programs/ProgramContext";
import ProgramsForm from "../../../components/custom/programs/ProgramsForm";
import { PROGRAM_DEFAULTS } from "../../../components/custom/programs/constants";
import { apiToTaggingForm, taggingSpecToApi } from "../../../components/custom/programs/taggingSpecAdapter";
import { errorToast } from "../../../utils/toasts";
import { InputField, DateField, Label } from "../../../components/custom/programs/fields";
import { Select, SelectItem } from "../../../components/fields/base/Select";
import {
  TAGGER_ENTITY_TYPE_LABELS,
  TAGGER_ENTITY_TYPE_OPTIONS,
} from "../../../components/custom/programs/taggingEntityTypes";
import {
  type TaggerRow,
  taggersQueryOptions,
  useCreateTagger,
  useUpdateTagger,
  useDeleteTagger,
} from "../api/useTaggers";

const columnHelper = createAppColumnHelper<TaggerRow>();

function TaggingRulesTable({
  onSelect,
  children,
}: {
  onSelect: (taggerId: number) => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation(["advertising", "common"]);
  const dateFormatter = useDateFormatter("medium");
  const userCanUpdate = useSimpleCan("update", "Ad");

  const defaultSelectedColumns = [
    "name",
    "entity_type",
    "is_enabled",
    "marketplace",
    "created_on",
    "updated_on",
    "run_count",
  ];

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "tagging_rules",
    initialState: {
      columnOrder: defaultSelectedColumns,
      sorting: [{ id: "name", desc: false }],
    },
  });

  const taggersQuery = useQuery(taggersQueryOptions());
  const deleteTagger = useDeleteTagger();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(($) => $.advertising["Name"]),
        size: 300,
        cell: ({ getValue, row }) => (
          <div
            className={css({
              cursor: "pointer",
              color: "#2e0054",
              fontWeight: 500,
              fontSize: "1.5rem",
              height: "5rem",
              display: "flex",
              alignItems: "center",
            })}
            onClick={() => onSelect(row.original.id)}
          >
            {getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("is_enabled", {
        header: t(($) => $.advertising["Enabled"]),
        cell: function EnabledCell({ getValue, row }) {
          const isEnabled = getValue();
          const [pendingState, setPendingState] = useState<boolean | null>(null);
          const switchState = pendingState ?? isEnabled;

          const toggle = useUpdateTagger();

          return (
            <Switch
              isSelected={switchState}
              isPending={toggle.isPending}
              onChange={(value) => {
                setPendingState(value);
                const { program } = row.original;
                toggle.mutate(
                  {
                    tagger_id: row.original.id,
                    is_enabled: value,
                    entity_type: row.original.entity_type,
                    spec: row.original.spec as Record<string, unknown>,
                    start_date: program.start_date ?? undefined,
                    end_date: program.end_date ?? undefined,
                  },
                  {
                    onSettled: () => {
                      setPendingState(null);
                    },
                  },
                );
              }}
              isDisabled={!userCanUpdate}
            />
          );
        },
      }),
      columnHelper.accessor("entity_type", {
        header: t(($) => $.advertising["Entity Type"]),
        cell: ({ getValue }) => t(TAGGER_ENTITY_TYPE_LABELS[getValue()]),
      }),
      columnHelper.accessor("marketplace", {
        header: t(($) => $.advertising["Marketplace"]),
      }),
      columnHelper.accessor("created_on", {
        header: t(($) => $.advertising["Created"]),
        cell: ({ getValue }) => {
          const v = getValue();
          return v ? dateFormatter.format(new Date(v)) : t(($) => $.advertising["N/A"]);
        },
      }),
      columnHelper.accessor("updated_on", {
        header: t(($) => $.advertising["Updated"]),
        cell: ({ getValue }) => {
          const v = getValue();
          return v ? dateFormatter.format(new Date(v)) : t(($) => $.advertising["N/A"]);
        },
      }),
      columnHelper.accessor((row) => row.program.run_cnt, {
        id: "run_count",
        header: t(($) => $.advertising["# Runs"]),
      }),
    ],
    [onSelect, userCanUpdate, dateFormatter, t],
  );

  const table = useClientTable({
    data: taggersQuery.data,
    columns,
    getRowId: (row) => String(row.id),
    enableRowSelection: userCanUpdate,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: taggersQuery.isFetching,
    },
  });

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  return (
    <div className={css({ width: "full" })}>
      <HStack justifyContent="end" gap={4} padding="small" width="full">
        {children}
        {userCanUpdate && (
          <BulkActionButton
            selected={selected}
            options={[
              {
                label: t(($) => $.advertising["Delete selected programs"], { count: selected.length }),
                action: async () => {
                  try {
                    await Promise.all(selected.map(async (id) => await deleteTagger.mutateAsync(Number(id))));
                  } catch {
                    errorToast({ message: t(($) => $.advertising["Failed to delete one or more tagging programs"]) });
                  }
                },
                confirmationMessage: (
                  <p>
                    {t(($) => $.advertising["Are you sure you want to delete the selected tagging programs?"], {
                      count: selected.length,
                    })}
                  </p>
                ),
              },
            ]}
          />
        )}
      </HStack>
      <HStack padding="small" fontSize="large">
        <VStack flex="1" alignItems="start">
          <HStack width="100%">
            <Button variant="contentOnly" onPress={() => resetTable(table)} fontSize="xxlarge">
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({
          maxWidth: "800px",
          tableLayout: "fixed",
          width: "max-content",
          minWidth: "100%",
        })}
      />
    </div>
  );
}

interface TaggingRulesFormProps {
  tagger: TaggerRow | null;
  onCancel: () => void;
  onSave: () => void;
}

function TaggingRulesForm({ tagger, onCancel, onSave }: TaggingRulesFormProps) {
  const { t } = useTranslation(["advertising", "common"]);
  const userCanUpdate = useSimpleCan("update", "Ad");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const dt = new Date().toISOString();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form components are untyped JS
  const defaultValues: Record<string, any> = (() => {
    if (!tagger) {
      return { ...PROGRAM_DEFAULTS["tagging"], name: "", entity_type: "advertisement", start_date: dt };
    }

    const formSpec = apiToTaggingForm(tagger.spec);

    return {
      ...PROGRAM_DEFAULTS["tagging"],
      ...formSpec,
      name: tagger.name,
      entity_type: tagger.entity_type,
      start_date: tagger.program.start_date ?? dt,
      end_date: tagger.program.end_date ?? null,
    };
  })();

  const { handleSubmit, ...form } = useForm({
    shouldUnregister: false,
    defaultValues,
    mode: "onChange",
  });

  const createTagger = useCreateTagger();
  const updateTagger = useUpdateTagger();

  const handleSave = (data: typeof defaultValues) => {
    const { name, entity_type, start_date, end_date, ...rawSpec } = data;
    const apiSpec = taggingSpecToApi(rawSpec as Parameters<typeof taggingSpecToApi>[0]);
    const spec = {
      ...apiSpec,
      function: "ADD_TAG" as const,
      condition: apiSpec.condition as "any" | "all" | "everything",
    };

    if (tagger) {
      updateTagger.mutate(
        {
          tagger_id: tagger.id,
          name,
          is_enabled: tagger.is_enabled,
          entity_type: tagger.entity_type,
          spec,
          start_date,
          end_date: end_date ?? undefined,
        },
        { onSuccess: onSave },
      );
    } else {
      createTagger.mutate(
        {
          name,
          marketplace: marketPlace,
          entity_type,
          start_date,
          end_date: end_date ?? null,
          spec,
        },
        { onSuccess: onSave },
      );
    }
  };

  const isPending = createTagger.isPending || updateTagger.isPending;

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      <VStack gap={4} alignItems="start" width="full">
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "50% 150px 150px",
            gap: "large",
            width: "full",
            marginBottom: "medium",
          })}
        >
          <InputField
            label={t(($) => $.advertising["Name"])}
            toolTip={null}
            placeholder={t(($) => $.advertising["Name"])}
            fieldName="name"
            errors={form.formState.errors}
            control={form.control}
            rules={{ required: { value: true, message: t(($) => $.advertising["Name is required"]) } }}
            disabled={false}
            format={null}
          />
          <DateField
            label={t(($) => $.advertising["Start Date"])}
            toolTip={null}
            placeholder={t(($) => $.advertising["Starting on..."])}
            fieldName="start_date"
            errors={form.formState.errors}
            control={form.control}
            rules={{ required: { value: true, message: t(($) => $.advertising["Start date is required"]) } }}
            disabled={false}
            minDate={null}
            maxDate={null}
          />
          <DateField
            label={t(($) => $.advertising["End Date (optional)"])}
            toolTip={null}
            placeholder={t(($) => $.advertising["Ending on..."])}
            fieldName="end_date"
            errors={form.formState.errors}
            control={form.control}
            rules={{}}
            disabled={false}
            minDate={null}
            maxDate={null}
          />
        </div>
        <Controller
          control={form.control}
          name="entity_type"
          rules={{ required: true }}
          render={({ field }) => (
            <div className={css({ width: "200px" })}>
              <Label>{t(($) => $.advertising["Entity Type"])}</Label>
              <Select
                selectedKey={field.value}
                onSelectionChange={(key) => field.onChange(key)}
                isDisabled={tagger != null}
                aria-label={t(($) => $.advertising["Entity Type"])}
              >
                {TAGGER_ENTITY_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} id={option.value} textValue={t(option.label)}>
                    {t(option.label)}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}
        />
        <div className={css({ width: "full" })}>
          <ProgramsForm {...form} hideHeader fieldName={undefined} isReadOnly={false} />
        </div>
      </VStack>
      <HStack gap={4} justifyContent="end" marginTop="2rem">
        <Button
          variant="secondary"
          onPress={() => {
            form.reset();
            onCancel();
          }}
        >
          {t(($) => $.common.actions.Cancel)}
        </Button>
        {userCanUpdate && (
          <Button
            variant="primary"
            isPending={isPending}
            onPress={async () => await handleSubmit(handleSave)()}
            isDisabled={!form.formState.isValid}
          >
            {t(($) => $.common.actions.Save)}
          </Button>
        )}
      </HStack>
    </FormProvider>
  );
}

export function TaggingRules() {
  const { t } = useTranslation(["advertising"]);
  const [selectedTagger, setSelectedTagger] = useState<TaggerRow | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const userCanUpdate = useSimpleCan("update", "Ad");
  const taggersQuery = useQuery(taggersQueryOptions());

  return (
    <ProgramContextProvider engine="tagging" size="lg" showHeaderFields={false}>
      {!isCreating && !selectedTagger && (
        <VStack gap={4} justifyContent="start" alignItems="start">
          <h3 className={css({ fontWeight: "bold", paddingBottom: "medium", paddingTop: "xsmall" })}>
            {t(($) => $.advertising["Tagging Programs"])}
          </h3>
          <TaggingRulesTable
            onSelect={(id) => {
              const row = taggersQuery.data?.find((t) => t.id === id);
              if (row) {
                setSelectedTagger(row);
              }
            }}
          >
            {userCanUpdate && (
              <Button onPress={() => setIsCreating(true)} fontSize="small">
                {t(($) => $.advertising["Create"])}
              </Button>
            )}
          </TaggingRulesTable>
        </VStack>
      )}
      {(isCreating || selectedTagger) && (
        <>
          <HStack justifyContent="space-between" className={css({ width: "full", marginBottom: "xlarge" })}>
            <HStack gap={4} justifyContent="start" className={`fs-standard ${css({ width: "full" })}`}>
              <Button
                variant="link"
                onPress={() => {
                  setIsCreating(false);
                  setSelectedTagger(null);
                }}
              >
                {t(($) => $.advertising["Tagging Programs"])}
              </Button>
              /{" "}
              <b>
                {isCreating ?
                  t(($) => $.advertising["Create Tagging Program"])
                : t(($) => $.advertising["Update Tagging Program"])}
              </b>
            </HStack>
          </HStack>
          <TaggingRulesForm
            tagger={isCreating ? null : selectedTagger}
            onCancel={() => {
              setIsCreating(false);
              setSelectedTagger(null);
            }}
            onSave={() => {
              setIsCreating(false);
              setSelectedTagger(null);
            }}
          />
        </>
      )}
    </ProgramContextProvider>
  );
}
